import type { DocsPageMeta } from '~/utils/docs-manifest';
import { fetchDocsPage } from '~/utils/docs-manifest';
import { extractDocsTitleFromRaw, formatDocsNavLabel } from '~/utils/docs-nav-tree';
import { renderDocHtml } from '~/utils/docs-markdown';

export function useDocsPage(logicalPath: MaybeRef<string>) {
  const docPage = ref<DocsPageMeta | undefined>();
  const loading = ref(false);
  const notFound = ref(false);

  watch(
    () => toValue(logicalPath),
    async path => {
      if (!path) {
        docPage.value = undefined;
        notFound.value = false;
        return;
      }

      loading.value = true;
      notFound.value = false;
      try {
        docPage.value = await fetchDocsPage(path);
        notFound.value = !docPage.value;
      } finally {
        loading.value = false;
      }
    },
    { immediate: true },
  );

  const rendered = computed(() => {
    const page = docPage.value;
    if (!page) {
      return { html: '', toc: [] as ReturnType<typeof renderDocHtml>['toc'] };
    }
    return renderDocHtml(page.raw, page.folderPath);
  });

  const docTitle = computed(() => {
    const page = docPage.value;
    if (!page) {
      return '';
    }
    const fromMd = extractDocsTitleFromRaw(page.raw);
    if (fromMd) {
      return fromMd;
    }
    const parts = toValue(logicalPath).split('/');
    return formatDocsNavLabel(parts[parts.length - 1] || parts[0]);
  });

  return {
    docPage,
    loading,
    notFound,
    rendered,
    docTitle,
  };
}
